import { DialogContent } from "@mui/material";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { doc, getDoc } from "firebase/firestore";
import * as React from "react";
import { toast } from "react-toastify";
import { database } from "../../firebase";
import { updateEnvioCantidadPaquetes } from "../../firebase/firestore/envios";
import {
  savePaquete,
  updatePaquete,
  type Paquete,
  type PaqueteDto,
} from "../../firebase/firestore/paquetes";
import { updateUsuario, type Usuario } from "../../firebase/firestore/usuarios";
import {
  camposFaltantesSegunEstado,
  esEstadoEntregado,
  mensajeEntregaIncompleta,
} from "../../utils/entregaValidations";
import { useAgenciaStore } from "../../zustand/useAgenciaStore";
import { usePaqueteStore } from "../../zustand/usePaquetesStore";
import type { PaqueteForm } from "./PackageItem";
import StepPaquetes from "./StepPaquetes";
import StepResumen from "./StepResumen";
import StepUsuario from "./StepUsuarios";

const steps = ["Datos del usuario", "Datos Paquetes", "Detalles"];

/** Indice del ultimo paso: el resumen con toda la informacion del paquete. */
export const STEP_RESUMEN = steps.length - 1;

type FormData = {
  usuario: Usuario | undefined;
  paquetes: PaqueteForm[];
};

const normalizarPaquete = (paquete: PaqueteForm, idUsuario: string): Paquete =>
  ({
    ...paquete,
    idUsuario,
    cobroAdicional: paquete.cobroAdicional ?? 0,
    peso: {
      monto: paquete.peso?.monto ?? 0,
      unidad: paquete.peso?.unidad ?? "lb",
    },
  } as Paquete);

export default function FormPackage({
  entity,
  onClose,
  initialStep = 0,
}: {
  entity?: PaqueteDto;
  onClose: () => void;
  /** Paso en el que se abre el wizard. `STEP_RESUMEN` para consultar el paquete. */
  initialStep?: number;
}) {
  const { agencia } = useAgenciaStore();
  const [isLoading, setIsLoading] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(initialStep);
  const [formData, setFormData] = React.useState<FormData>({
    usuario: entity?.usuario ?? undefined,
    paquetes: [
      {
        id: entity?.id ?? "",
        idRastreo: entity?.idRastreo ?? "",
        via: entity?.via ?? "aereo",
        contenido: entity?.contenido ?? undefined,
        ubicacionFisica: entity?.ubicacionFisica ?? undefined,
        tarifa: entity?.tarifa ?? undefined,
        peso: entity?.peso ?? {
          monto: 0,
          unidad: "lb",
        },
        cobroAdicional: entity?.cobroAdicional ?? 0,
        rastreo: entity?.rastreo ?? [],
        estado: entity?.estado ?? (agencia?.estados?.[0]?.id || "recibido"),
        createdAt: entity?.createdAt,
        updatedAt: entity?.updatedAt,
        observaciones: entity?.observaciones,
        costo: entity?.costo,
        ganancia: entity?.ganancia,
        detallesEntrega: entity?.detallesEntrega,
        puntosAcumulados: entity?.puntosAcumulados,
        puntosRedimidos: entity?.puntosRedimidos,
        descuento: entity?.descuento,
        envio: entity?.envio,
      },
    ],
  });
  const { fetchAllPaquetes, fetchCounts, resetPackages } = usePaqueteStore();

  const handleNext = () =>
    setActiveStep((prevActiveStep) => prevActiveStep + 1);

  const handleBack = () =>
    setActiveStep((prevActiveStep) => prevActiveStep - 1);

  const handleUsuario = (usuario: Usuario) => {
    setFormData((prev) => {
      // Actualizar tarifas de paquetes nuevos según el nuevo usuario
      const paquetesActualizados = prev.paquetes.map((paquete) => {
        // Solo procesar paquetes nuevos (sin id)
        if (!paquete.id) {
          const tarifaPredeterminada = 
            paquete.via === "aereo" 
              ? usuario?.tarifaAerea 
              : paquete.via === "maritimo"
              ? usuario?.tarifaMaritima
              : undefined;
          
          // Si el nuevo usuario tiene tarifa predeterminada, aplicarla
          // Si no tiene tarifa predeterminada, limpiar la tarifa anterior
          return { ...paquete, tarifa: tarifaPredeterminada };
        }
        // Para paquetes existentes, no modificar la tarifa
        return paquete;
      });
      
      return { ...prev, usuario, paquetes: paquetesActualizados };
    });
  };

  const handlePaquetes = (paquetes: PaqueteForm[]) =>
    setFormData((prev) => ({ ...prev, paquetes }));

  const handleSave = async () => {
    if (!formData.usuario || formData.paquetes.length === 0) return;

    // Un paquete entregado necesita peso y tarifa para calcular el total y los
    // puntos, asi que se bloquea el guardado si falta alguno.
    for (const paquete of formData.paquetes) {
      const faltantes = camposFaltantesSegunEstado(
        agencia,
        paquete.estado,
        paquete
      );
      if (faltantes.length > 0) {
        toast.error(
          `${mensajeEntregaIncompleta(faltantes)}${
            paquete.idRastreo ? ` (${paquete.idRastreo})` : ""
          }`
        );
        return;
      }
    }

    setIsLoading(true);

    const resultados = {
      exitosos: 0,
      fallidos: 0,
    };

    try {
      const enviosToUpdate = new Set<string>();
      // Los puntos acumulados por paquete propio se acreditan y registran en el
      // servidor (Cloud Function `acumularPuntosReferido`). Aquí solo se ajustan
      // los puntos redimidos.
      let puntosRedimidosTotal = 0;

      const esEntregado = (estado?: string) =>
        esEstadoEntregado(agencia, estado);

      for (const paquete of formData.paquetes) {
        try {
          const paqueteData = normalizarPaquete(paquete, formData.usuario.id);

          if (paquete.id) {
            // Si es edición, obtener datos anteriores del paquete
            const paqueteDocRef = doc(database, "paquetes", paquete.id);
            const paqueteDocSnap = await getDoc(paqueteDocRef);
            const oldPaqueteData = paqueteDocSnap.exists() 
              ? { id: paqueteDocSnap.id, ...paqueteDocSnap.data() } as Paquete
              : null;
            
            // Verificar si el envío cambió
            if (oldPaqueteData?.envio && oldPaqueteData.envio !== paqueteData.envio) {
              enviosToUpdate.add(oldPaqueteData.envio);
            }
            if (paqueteData.envio) {
              enviosToUpdate.add(paqueteData.envio);
            }
            
            // Calcular diferencia de puntos redimidos (los acumulados los
            // gestiona el servidor).
            const estadoAnterior = oldPaqueteData?.estado;
            const estadoNuevo = paquete.estado;
            const puntosRedimidosAnteriores = esEntregado(estadoAnterior)
              ? (oldPaqueteData?.puntosRedimidos ?? 0)
              : 0;
            const puntosRedimidosNuevos = esEntregado(estadoNuevo)
              ? (paquete.puntosRedimidos ?? 0)
              : 0;

            puntosRedimidosTotal += puntosRedimidosNuevos - puntosRedimidosAnteriores;

            await updatePaquete(paqueteData);
          } else {
            await savePaquete(paqueteData);
            if (paqueteData.envio) {
              enviosToUpdate.add(paqueteData.envio);
            }
            
            // Si es nuevo y está entregado, restar los puntos redimidos.
            if (esEntregado(paquete.estado)) {
              puntosRedimidosTotal += paquete.puntosRedimidos ?? 0;
            }
          }

          resultados.exitosos++;
        } catch (error) {
          resultados.fallidos++;
          console.error(`Error al guardar paquete`, paquete, error);
          toast.error(
            `Error en paquete con id de rastreo ${paquete.idRastreo}`
          );
        }
      }
      
      // Descontar los puntos redimidos del usuario si hubo cambios.
      if (puntosRedimidosTotal !== 0 && formData.usuario) {
        const puntosActuales = formData.usuario.puntos ?? 0;
        const nuevosPuntos = puntosActuales - puntosRedimidosTotal;
        await updateUsuario(formData.usuario.id, { puntos: Math.max(0, nuevosPuntos) });
      }

      // Actualizar conteos de envíos afectados
      for (const nombreEnvio of Array.from(enviosToUpdate)) {
        try {
          await updateEnvioCantidadPaquetes(nombreEnvio);
        } catch (error) {
          console.error(`Error actualizando envío ${nombreEnvio}:`, error);
        }
      }

      if (resultados.fallidos === 0) {
        toast.success(
          `${resultados.exitosos} paquete${
            resultados.exitosos > 1 ? "s" : ""
          } guardado${resultados.exitosos > 1 ? "s" : ""} con éxito`
        );
      } else {
        toast.error(
          `${resultados.fallidos} error${
            resultados.fallidos > 1 ? "es" : ""
          }, ${resultados.exitosos} guardado${
            resultados.exitosos > 1 ? "s" : ""
          } con éxito`
        );
      }
    } catch (error) {
      console.error("Error inesperado al guardar paquetes:", error);
      toast.error("Ocurrió un error inesperado");
    } finally {
      // Reset and refresh data to ensure we get the latest information
      resetPackages();
      await Promise.all([fetchAllPaquetes(), fetchCounts()]);
      setIsLoading(false);
      onClose();
    }
  };

  return (
    <DialogContent sx={{ maxWidth: "100%" }}>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <StepUsuario
          selectedUser={formData.usuario}
          setSelectedUser={handleUsuario}
          handleNext={handleNext}
          handleBack={handleBack}
          activeStep={activeStep}
        />
      )}

      {activeStep === 1 && formData.usuario && (
        <StepPaquetes
          paquetes={formData.paquetes}
          setPaquetes={handlePaquetes}
          handleNext={handleNext}
          handleBack={handleBack}
          activeStep={activeStep}
          usuario={formData.usuario}
        />
      )}

      {activeStep === 2 && formData.usuario && (
        <StepResumen
          selectedUser={formData.usuario}
          paquetes={formData.paquetes}
          handleBack={handleBack}
          handleSave={handleSave}
          isLoading={isLoading}
        />
      )}
    </DialogContent>
  );
}
